// Quote Builder — bottom action bar.
//
// Save / Customer PDF / Internal PDF / Send to customer / Place order.
//
// Save is a no-op for the autosaving store — we just toast confirmation.
// PDF buttons route to pdf.ts (print-stylesheet path; no jsPDF dep).
// Send to customer POSTs to /api/quotes/send (kitchenos-api customer_portal
// modules:964). Place order opens the place-order modal.

import { ApiError } from "@tkos/api-client";
import { confirmDialog } from "@tkos/shared-ui";
import type { QuoteStore } from "./state";
import { toWire } from "./state";
import type { PageContext } from "../../shell/types";
import { quoteTotals } from "./pricing";
import { fmtPence } from "./formatters";
import { exportCustomerPdf, exportInternalPdf } from "./pdf";
import { customerLineDescription } from "./customer-copy";
import { openPlaceOrder } from "./place-order";
import { invalidateCustomerCache } from "./customer-typeahead";

export function mountActions(
  host: HTMLElement,
  store: QuoteStore,
  ctx: PageContext,
): () => void {
  let inflight: AbortController | null = null;

  host.innerHTML = `
    <div class="qb-actions">
      <button type="button" class="qb-btn qb-btn--ghost" data-role="save" title="Save (Ctrl+S)">Save</button>
      <button type="button" class="qb-btn qb-btn--ghost" data-role="customer-pdf" title="Customer PDF (Ctrl+P)">Customer PDF</button>
      <button type="button" class="qb-btn qb-btn--ghost" data-role="internal-pdf">Internal PDF</button>
      <button type="button" class="qb-btn qb-btn--ghost" data-role="close-quote" title="Close this quote and return to your customers">Close quote</button>
      <button type="button" class="qb-btn qb-btn--success qb-actions__push" data-role="send">Send to customer</button>
      <button type="button" class="qb-btn qb-btn--primary" data-role="place">Place order</button>
      <button type="button" class="qb-btn qb-btn--danger" data-role="delete" title="Delete this quote">Delete quote</button>
    </div>
  `;

  // "Send to customer" becomes "Send order" once the customer has approved the
  // quote. Relabel only — the action is unchanged (a scoped order flow is a
  // separate task). Kept in sync with the store status.
  const sendBtn = host.querySelector<HTMLButtonElement>("[data-role='send']");
  function updateSendLabel(): void {
    if (sendBtn) sendBtn.textContent = store.get().status === "approved" ? "Send order" : "Send to customer";
  }
  updateSendLabel();
  const offStatus = store.subscribe(() => updateSendLabel());

  host.querySelector<HTMLButtonElement>("[data-role='save']")?.addEventListener("click", () => {
    // The store autosaves; this is mostly user-confirmation feedback.
    const t = quoteTotals(store.get());
    ctx.showToast(`Saved · ${fmtPence(t.grandTotalPence)} inc VAT`, "success");
  });

  host.querySelector<HTMLButtonElement>("[data-role='customer-pdf']")?.addEventListener("click", () => {
    const q = store.get();
    exportCustomerPdf(q, ctx);
    recordPdfSnapshot(q, "customer");
  });

  host.querySelector<HTMLButtonElement>("[data-role='internal-pdf']")?.addEventListener("click", () => {
    const q = store.get();
    exportInternalPdf(q, ctx);
    recordPdfSnapshot(q, "internal");
  });

  // The PDF is made in the browser and never reaches the server, so tell the
  // server what it contained: an immutable snapshot of the lines, totals and
  // margin at export (migration 0098). Fire-and-forget — a quote that has not
  // synced yet has no server row to attach it to, and a failure here must never
  // get in the way of the PDF itself. Identical repeats are skipped server-side.
  function recordPdfSnapshot(q: ReturnType<QuoteStore["get"]>, kind: "customer" | "internal"): void {
    if (!q.serverId) return;
    void ctx.api(`/portal/quote-builder/${encodeURIComponent(q.serverId)}/snapshots`, {
      method: "POST",
      body: JSON.stringify({ kind, quote: toWire(q) }),
    }).catch((err: unknown) => console.warn("[qb actions] PDF snapshot not recorded", err));
  }

  host.querySelector<HTMLButtonElement>("[data-role='send']")?.addEventListener("click", () => void sendToCustomer());
  host.querySelector<HTMLButtonElement>("[data-role='place']")?.addEventListener("click", () => openPlaceOrder(store, ctx));
  host.querySelector<HTMLButtonElement>("[data-role='delete']")?.addEventListener("click", () => void deleteQuote());
  host.querySelector<HTMLButtonElement>("[data-role='close-quote']")?.addEventListener("click", () => void closeQuote());

  async function closeQuote(): Promise<void> {
    // Close = stop working on this quote and return to customers. Unlike
    // Delete, the quote is left intact (autosave has already persisted it).
    if (!(await confirmDialog(
      "Close this quote and return to your customers? The quote is saved and stays available.",
      { title: "Close quote", confirmLabel: "Close quote" },
    ))) return;
    // Prefer the customer this quote belongs to; fall back to the CRM list.
    const backTo = (store.get().customerId || "").trim();
    // Forget the local working copy so the next "New quote" opens blank rather
    // than resuming the quote just closed. See QuoteStore.resetLocal.
    store.resetLocal();
    if (backTo) ctx.navigate("customer", { id: backTo });
    else ctx.navigate("crm");
  }

  async function deleteQuote(): Promise<void> {
    const q = store.get();
    if (!(await confirmDialog(
      "Delete this quote permanently? This cannot be undone.",
      { title: "Delete quote", confirmLabel: "Delete quote", destructive: true },
    ))) return;
    // A quote that never synced to PG has no serverId — nothing to delete
    // server-side; just clear it locally. This genuinely clears it now: the
    // local snapshot used to survive, so the discarded quote came back on the
    // next "New quote" and the autosave then POSTed it as a brand-new row.
    if (!q.serverId) {
      store.resetLocal();
      ctx.showToast("Quote discarded", "info");
      ctx.navigate("crm");
      return;
    }
    try {
      await ctx.api<{ ok?: boolean }>(`/portal/quote-builder/${encodeURIComponent(q.serverId)}`, {
        method: "DELETE",
      });
      invalidateCustomerCache();
      ctx.track({ eventType: "action", feature: "quote-builder", customData: { action: "quote-deleted" } });
      // Drop the local copy too — otherwise the deleted quote is still the
      // resumed working copy and the next autosave re-creates it server-side.
      store.resetLocal();
      ctx.showToast("Quote deleted", "success");
      ctx.navigate("crm");
    } catch (err) {
      const msg = err instanceof ApiError && err.status === 404
        ? "Quote already removed"
        : "Could not delete quote";
      ctx.showToast(msg, "error");
    }
  }

  // ── Keyboard shortcuts ──────────────────────────────────────────────────
  const onKeyDown = (e: KeyboardEvent): void => {
    if (!(e.ctrlKey || e.metaKey)) return;
    const target = e.target as HTMLElement | null;
    const inField = target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;
    switch (e.key.toLowerCase()) {
      case "s":
        if (inField) return; // let the browser handle save-page if in a field? Actually we want to intercept.
        e.preventDefault();
        host.querySelector<HTMLButtonElement>("[data-role='save']")?.click();
        break;
      case "p":
        e.preventDefault();
        host.querySelector<HTMLButtonElement>("[data-role='customer-pdf']")?.click();
        break;
    }
  };
  document.addEventListener("keydown", onKeyDown);

  async function sendToCustomer(): Promise<void> {
    const q = store.get();
    if (!q.customer) {
      ctx.showToast("Add a customer name first", "error");
      return;
    }
    const t = quoteTotals(q);
    if (t.lineCount === 0) {
      ctx.showToast("Add at least one line item before sending", "error");
      return;
    }

    // ── Auto-CRM: if the retailer typed a free-text customer name (no
    // customerId from the typeahead) and the "Save as new CRM contact"
    // checkbox is still ticked, POST /api/crm/contacts BEFORE sending the
    // quote so the resulting CRM record is linked. Skipped silently on
    // failure — quote send still proceeds with body-supplied details.
    let customerId = q.customerId || "";
    const autoCrmEl = document.querySelector<HTMLInputElement>("[data-meta-autocrm]");
    const shouldAutoCreate = !customerId && (autoCrmEl?.checked ?? false);
    if (shouldAutoCreate) {
      try {
        // The server returns the upserted contact(s) under the plural
        // `contacts` key (modules/crm.js POST /contacts:735). Reading the
        // singular `contact` field (legacy assumption) was always
        // returning undefined, so customerId never got written back and
        // the saved quote ended up with `customer_id = NULL`.
        interface CreatedRes {
          ok?: boolean;
          contacts?: Array<{ id?: string; name?: string }>;
        }
        const created = await ctx.api<CreatedRes>("/crm/contacts", {
          method: "POST",
          body: JSON.stringify({
            contact: {
              name: q.customer,
              email: q.customerEmail ?? "",
              phone: q.customerPhone ?? "",
              project: q.project ?? "",
              // Seed at Lead so the contact shows up in the pipeline even if
              // /quotes/send fails. /quotes/send bumps to Quoted on success,
              // so the natural-state ordering (Lead → Quoted) is preserved.
              stage: "lead",
            },
          }),
        });
        const createdContact = created.contacts?.[0];
        if (createdContact?.id) {
          customerId = createdContact.id;
          store.set((d) => { d.customerId = customerId; });
          invalidateCustomerCache();
          // Broadcast so any open CRM page refreshes its pipeline without
          // a manual reload. The shared add-customer modal already
          // dispatches this event; the QB auto-CRM flow was missing it.
          window.dispatchEvent(
            new CustomEvent("tkos:customer-created", { detail: { contact: createdContact } }),
          );
          ctx.track({ eventType: "action", feature: "crm", customData: { action: "contact-created", via: "quote-builder" } });
        }
      } catch (err) {
        console.warn("[qb actions] auto-CRM create failed", err);
        // not fatal — fall through to send
      }
    }

    // Customer-facing line items. A zone flipped to "summary" is collapsed to a
    // single plain-English line (the retailer's description) so the customer's
    // emailed/portal PDF and quote view show the summary, never the SKUs. The
    // zone total is preserved, so the grand total / VAT are unchanged. The
    // internal/retailer breakdown is untouched (it reads zones directly).
    const items = q.zones.flatMap((z) => {
      if (q.zoneSummary[z.id] === true) {
        const zoneTotal = z.items.reduce((s, it) => s + (it.qty || 1) * (it.retailPrice || 0), 0);
        const desc = (z.summary || "").trim()
          || `${z.name} — ${z.items.length} item${z.items.length === 1 ? "" : "s"}`;
        return [{ desc, qty: 1, unit: zoneTotal, total: zoneTotal }];
      }
      return z.items.map((it) => ({
        // Fabrication detail stripped — hole counts and hinge sides are for
        // the factory, and this description is what the customer portal shows
        // AND what the server renders into the emailed PDF. Shared with the
        // customer PDF so the two documents describe a line the same way.
        desc: customerLineDescription(it) || it.code || "Item",
        qty: it.qty || 1,
        unit: it.retailPrice || 0,
        total: (it.qty || 1) * (it.retailPrice || 0),
      }));
    });
    const payload = {
      quote: {
        ref: q.reference,
        id: q.reference,
        title: q.project || `${q.customer} Kitchen Quote`,
        customer: q.customer,
        project: q.project || "",
        date: q.date,
        total: t.grandTotalPence / 100,
        sub: t.retailSubPence / 100,
        // Combined VAT (line items + delivery) so the customer's
        // Subtotal + Delivery + VAT reconciles to the grand total — exactly
        // how the retailer totals panel presents it (BUG 5).
        vat: (t.vatAmountPence + t.deliveryVatPence) / 100,
        // Delivery as its own ex-VAT charge so it shows as a clearly
        // labelled line in the customer quote (it was previously dropped,
        // leaving a silent gap between subtotal+VAT and the total).
        delivery: t.deliveryPence / 100,
        vatRate: q.vatRate,
        validDays: q.validDays,
        introNote: q.introNote ?? "",
        closingNote: q.closingNote ?? "",
        items,
      },
      // The full Quote Builder state behind this document (trade prices,
      // markup, margin), recorded server-side in an immutable snapshot next to
      // the customer copy (migration 0098). Never shown to the customer: the
      // server builds the customer copy from `quote` above, field by field.
      internal: toWire(q),
      customerName: q.customer,
      customerId,
      customerEmail: q.customerEmail ?? "",
      customerPhone: q.customerPhone ?? "",
    };

    inflight?.abort();
    inflight = new AbortController();
    try {
      const res = await ctx.api<{ ok?: boolean; ref?: string; error?: string }>("/quotes/send", {
        method: "POST",
        body: JSON.stringify(payload),
        signal: inflight.signal,
      });
      if (res.ok === false) {
        ctx.showToast(`Portal push failed: ${res.error ?? "unknown error"}`, "error");
        return;
      }
      // If the server returned a canonical reference, adopt it.
      if (res.ref && typeof res.ref === "string" && res.ref !== q.reference) {
        store.set((d) => { d.reference = res.ref!; });
      }
      store.set((d) => { d.status = "sent"; });
      ctx.track({ eventType: "action", feature: "quote-builder", customData: { action: "quote-sent" } });
      ctx.showToast(`Quote delivered to ${q.customer} ✓ Their portal now shows this version.`, "success");
      // Audit hook — see place-order.ts for the same pattern.
      try {
        window.dispatchEvent(new CustomEvent("qb:quote-sent", {
          detail: { quoteId: q.id, reference: q.reference, customer: q.customer, at: Date.now() },
        }));
      } catch { /* ignore */ }
    } catch (err) {
      if ((err as DOMException)?.name === "AbortError") return;
      let msg = "Network error sending quote";
      if (err instanceof ApiError) {
        if (err.status === 404 || err.status === 501) {
          msg = "Quote-send endpoint not available on this server.";
        } else {
          // The server returns { ok:false, error } as the body; ApiError carries
          // it as the message. Surface the real reason (e.g. no email/phone on
          // file) instead of a bare HTTP code.
          try { msg = JSON.parse(err.message)?.error || `Quote send failed (HTTP ${err.status})`; }
          catch { msg = `Quote send failed (HTTP ${err.status})`; }
        }
      }
      ctx.showToast(msg, "error");
    } finally {
      inflight = null;
    }
  }

  return () => {
    inflight?.abort();
    offStatus();
    document.removeEventListener("keydown", onKeyDown);
  };
}
